import { BlankSlateInline } from "@msuite/ui/blank-slate";
import { Select } from "@msuite/ui/form";
import { useDisclosure } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { Fragment, responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { getArticleGroupNumberString } from "@msuite/utils/stock";
import type { Nullable } from "@msuite/utils/types";
import type { Control, FieldValues, Path } from "react-hook-form";
import { client } from "@/hono";
import { AddArticleProductTypeModal } from "@/modals/stock/add-article-product-type-modal/add-article-product-type-modal";

/** Props Interface */
interface ArticleProductTypeSelectProps<T extends FieldValues> {
	control: Control<T>;
	name: Path<T>;
	articleGroupId: Nullable<string>;
}

export function ArticleProductTypeSelect<T extends FieldValues>({
	control,
	name,
	articleGroupId,
}: ArticleProductTypeSelectProps<T>) {
	/** Hooks */
	const { t } = useTranslation();
	const addProductTypeModal = useDisclosure();

	/** Query */
	const { data, isFetching, refetch } = useQuery({
		enabled: !!articleGroupId,
		queryKey: ["article-product-types", articleGroupId || ""],
		queryFn: async () => {
			if (!articleGroupId) throw new Error("articleGroupId is required.");
			const response = await client.stock["article-groups"][":articleGroupId"][
				"article-product-types"
			].$get({
				param: { articleGroupId },
			});
			const data = await responseHandler(response);
			return data.articleProductTypes;
		},
	});

	if (!articleGroupId) return null;
	if (isFetching) return null;
	if (!guardArray(data).length)
		return (
			<Fragment>
				<BlankSlateInline
					variant="input"
					label={t("stock.article_group_has_no_product_types")}
					onClick={addProductTypeModal.onOpen}
				>
					{t("stock.article_product_type")}
				</BlankSlateInline>
				<AddArticleProductTypeModal
					isOpen={addProductTypeModal.isOpen}
					onClose={addProductTypeModal.onClose}
					onComplete={refetch}
					articleGroupId={articleGroupId}
				/>
			</Fragment>
		);

	/** Render */
	return (
		<Select
			label={t("stock.article_product_type")}
			control={control}
			name={name}
			isRequired
			placeholder={t("stock.select_product_type")}
			isNullable={false}
			defaultValue={guardArray(data)?.at(0)?.id as any}
			options={guardArray(data).map((item) => ({
				label: `${getArticleGroupNumberString(item.number)} / ${item.name}`,
				value: item.id,
				item: { item },
			}))}
			data-cy="product-type-select"
		/>
	);
}
